import React, { useState, useEffect } from 'react';
import { useRoutinesWithCache } from '../../shared';
import RoutineView from './RoutineView';
import EmptyRoutineState from './EmptyRoutineState';
import WeekdaySelector from './WeekdaySelector';
import RoutineDifficultySelector from './RoutineDifficultySelector';
import { useRoutineDifficulty } from '../hooks/useRoutineDifficulty';
import MyRoutinesView from './MyRoutinesView';
import PublicRoutinesView from './PublicRoutinesView';
import { RoutineService } from '../services/routineService';
import { WorkoutsService } from '../../workouts/workoutsService';
import type { WorkoutSession } from '../../workouts/types';
import WorkoutView from '../../workouts/components/WorkoutView';
import { AlertIcon } from '../../../assets/icons/index.tsx';
import { diaDeHoy } from '../utils/semana';
import { useMiSemana } from '../hooks/useMiSemana';

type Tab = 'today' | 'mine' | 'public';

const RoutinesContainer: React.FC = () => {
  const { currentRoutine, isLoading, error, loadRoutineByWeekday } =
    useRoutinesWithCache();
  // Qué día se está mirando. Arranca en hoy: es la rutina que toca.
  const [dia, setDia] = useState<number>(() => diaDeHoy());
  const { entrenados, marcarEntrenado } = useMiSemana();
  const [tab, setTab] = useState<Tab>(() => {
    const initial = sessionStorage.getItem('routines.initialTab');
    if (initial === 'mine' || initial === 'public' || initial === 'today') {
      sessionStorage.removeItem('routines.initialTab');
      return initial;
    }
    return 'today';
  });
  const [savingClone, setSavingClone] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneOk, setCloneOk] = useState<string | null>(null);
  const [workoutSession, setWorkoutSession] = useState<WorkoutSession | null>(null);
  const [startingWorkout, setStartingWorkout] = useState(false);
  const { difficulty, setDifficulty } = useRoutineDifficulty();

  // Al cambiar de día o de nivel hay que traer la rutina correspondiente: son
  // filas distintas en la BD, no un filtro en cliente.
  useEffect(() => {
    void loadRoutineByWeekday(dia, difficulty);
  }, [dia, difficulty, loadRoutineByWeekday]);

  const handleStartWorkout = async (routineId: string) => {
    setStartingWorkout(true);
    setCloneError(null);
    try {
      const session = await WorkoutsService.start(routineId, difficulty);
      setWorkoutSession(session);
      // La tira de la semana se marca ya, sin esperar a recargarla: el
      // entreno acaba de empezar y es hoy.
      marcarEntrenado(diaDeHoy());
    } catch (e) {
      setCloneError((e as Error).message);
    } finally {
      setStartingWorkout(false);
    }
  };

  const handleSaveAsMine = async (routineId: string) => {
    setSavingClone(true);
    setCloneError(null);
    setCloneOk(null);
    try {
      await RoutineService.cloneRoutineToMine(routineId);
      setCloneOk('Guardada en tus rutinas.');
    } catch (e) {
      setCloneError((e as Error).message);
    } finally {
      setSavingClone(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'today', label: 'Hoy' },
    { id: 'mine', label: 'Mis rutinas' },
    { id: 'public', label: 'Públicas' },
  ];

  if (workoutSession && currentRoutine) {
    return (
      <WorkoutView
        routine={currentRoutine}
        session={workoutSession}
        onFinish={() => setWorkoutSession(null)}
        onAbandon={() => setWorkoutSession(null)}
      />
    );
  }

  return (
    <div>
      <div className="flex gap-1 mb-4 border-b border-[#334155] overflow-x-auto">
        {tabs.map(({ id, label }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                active
                  ? 'border-[#1f9e3f] text-white'
                  : 'border-transparent text-[#94a3b8] hover:text-white'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'today' ? (
        <>
          {(cloneError || cloneOk) && (
            <div
              className={`p-3 rounded-lg text-sm mb-4 flex items-start gap-2 ${
                cloneError
                  ? 'bg-red-900/40 border border-red-500/50 text-red-300'
                  : 'bg-[#1f9e3f22] border border-[#1f9e3f55] text-[#6ee06f]'
              }`}
            >
              <span className="mt-0.5"><AlertIcon size={16} /></span>
              <span>{cloneError ?? cloneOk}</span>
            </div>
          )}

          <WeekdaySelector
            value={dia}
            // Sin `allowNone` el ciclo nunca pasa por null, pero el tipo lo
            // admite porque el mismo control coloca rutinas en «mis rutinas».
            onChange={(d) => setDia(d ?? dia)}
            entrenados={entrenados}
            marcarHoy
            disabled={isLoading}
          />

          <RoutineDifficultySelector
            value={difficulty}
            onChange={setDifficulty}
            disabled={isLoading}
          />

          {isLoading ? (
            <div className="flex items-center justify-center min-h-64 px-4">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 sm:h-10 lg:h-12 w-8 sm:w-10 lg:w-12 border-4 border-[#1f9e3f] border-t-transparent mx-auto mb-3 sm:mb-4"></div>
                <p className="text-gray-300 text-sm sm:text-base">
                  Cargando rutina...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center p-8">
              <div className="text-red-500 mb-4 flex justify-center">
                <AlertIcon size={40} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Error al cargar rutina
              </h3>
              <p className="text-gray-400 mb-4">{error}</p>
              {/* Sin esto la pantalla de error no tenía salida: había que
                  cambiar de pestaña o recargar la página entera. */}
              <button
                onClick={() => void loadRoutineByWeekday(dia, difficulty, true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[#1f9e3f] text-white hover:bg-[#1a8636] transition-colors"
              >
                Reintentar
              </button>
            </div>
          ) : currentRoutine ? (
            <RoutineView
              routine={currentRoutine}
              onSaveAsMine={(r) => handleSaveAsMine(r.id)}
              savingAsMine={savingClone}
              onStartWorkout={(r) => handleStartWorkout(r.id)}
              startingWorkout={startingWorkout}
            />
          ) : (
            <EmptyRoutineState />
          )}
        </>
      ) : tab === 'mine' ? (
        <MyRoutinesView />
      ) : (
        <PublicRoutinesView />
      )}
    </div>
  );
};

export default RoutinesContainer;
